import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown } from '../../../assets/icons'
import { Button } from '../../ui/button'
import Autocomplete from '../../ui/autoComplete'
import { Text2 } from '../../ui/text'
import SearchCheckListModal from '../../modal/searchCheckListModal'
import useIsMobile from '../../../hooks/useIsMobile'
import theme from '../../../styles/theme'
import {
	ButtonRow,
	FiltersContainer,
	FiltersGrid,
	GridItem,
	SelectButton,
	SelectLabel,
	SelectWrapper,
} from './styled'
import DateTimeRangeFilters from '../dateTimeRangeFilters'
import ClinicSelect from '../../clinicSelect'


const SPECIALTY_SELECTION_LIMIT = 3

const AppointmentMultipleFilters = ({
	values,
	errors,
	touched,
	handleSubmit,
	setFieldValue,
	specialties,
	clinics,
	onChangeSpeciality,
	onChangeClinic,
	specialtyPlaceholder = 'Seleccioná hasta 3 especialidades'
}) => {
	const isMobile = useIsMobile()
	const today = new Date()
	const dateRangeError = errors.startDate || errors.endDate
	const timeRangeError = errors.timeRange || errors.hourFrom || errors.hourTo
	const [showSpecialtyModal, setShowSpecialtyModal] = useState(false)
	const [specialtySearch, setSpecialtySearch] = useState('')
	const [hasSpecialtyLineBreak, setHasSpecialtyLineBreak] = useState(false)
	const specialtyTextRef = useRef(null)
	const [draftSpecialties, setDraftSpecialties] = useState(
		Array.isArray(values.specialities) && values.specialities.length > 0
			? values.specialities
			: values.speciality
			? [values.speciality]
			: []
	)

	const selectedSpecialtyLabels = specialties
		.filter(specialty =>
			(Array.isArray(values.specialities) && values.specialities.length > 0
				? values.specialities
				: values.speciality
				? [values.speciality]
				: []
			).includes(specialty.value)
		)
		.map(specialty => specialty.label)

	const selectedSpecialtyLabel = selectedSpecialtyLabels.join(', ')

	useEffect(() => {
		const checkLineBreak = () => {
			const textNode = specialtyTextRef.current
			if (!textNode) return
			const lineHeight = parseInt(getComputedStyle(textNode).lineHeight) || 20
			const hasBreak = textNode.scrollHeight > lineHeight * 1.3
			setHasSpecialtyLineBreak(hasBreak)
		}

		checkLineBreak()
		const timer = setTimeout(checkLineBreak, 100)
		window.addEventListener('resize', checkLineBreak)

		return () => {
			clearTimeout(timer)
			window.removeEventListener('resize', checkLineBreak)
		}
	}, [selectedSpecialtyLabel, isMobile])

	const filteredSpecialties = specialties.filter(s =>
		s.label.toLowerCase().includes(specialtySearch.toLowerCase())
	)

	const openSpecialtyModal = () => {
		setDraftSpecialties(
			Array.isArray(values.specialities) && values.specialities.length > 0
				? values.specialities
				: values.speciality
				? [values.speciality]
				: []
		)
		setSpecialtySearch('')
		setShowSpecialtyModal(true)
	}

	const closeSpecialtyModal = () => {
		setShowSpecialtyModal(false)
		setSpecialtySearch('')
	}

	const handleSelectSpecialty = option => {
		setDraftSpecialties(currentSpecialties => {
			if (currentSpecialties.includes(option.value)) {
				return currentSpecialties.filter(specialty => specialty !== option.value)
			}

			if (currentSpecialties.length >= SPECIALTY_SELECTION_LIMIT) {
				return currentSpecialties
			}

			return [...currentSpecialties, option.value]
		})
	}

	const handleConfirmSpecialty = () => {
		const selectedSpecialty = draftSpecialties[0] || ''

		if (
			selectedSpecialty !== values.speciality ||
			JSON.stringify(draftSpecialties) !== JSON.stringify(values.specialities || [])
		) {
			onChangeSpeciality(selectedSpecialty, setFieldValue, {
				specialities: draftSpecialties
			})
		}
		closeSpecialtyModal()
	}

	const handleRemoveDraftSpecialty = specialtyValue => {
		setDraftSpecialties(currentSpecialties =>
			currentSpecialties.filter(specialty => specialty !== specialtyValue)
		)
	}


	return (
		<>
			<FiltersContainer $isMobile={isMobile}>
				<FiltersGrid>
					<GridItem>
						<SelectWrapper>
							<SelectLabel>Especialidad</SelectLabel>
							<SelectButton
								type="button"
								$isEmpty={!values.speciality}
								$error={touched.speciality && errors.speciality}
								$hasLineBreak={hasSpecialtyLineBreak}
								onClick={openSpecialtyModal}
							>
								<span
									ref={specialtyTextRef}
									style={{
										flex: 1,
										whiteSpace: 'normal',
										lineHeight: '20px',
										marginRight: '8px'
									}}
								>
									<Text2 color={selectedSpecialtyLabel ? theme.colors.text.primaryText : theme.colors.greys.disabledGrey}>
										{selectedSpecialtyLabel || specialtyPlaceholder}
									</Text2>
								</span>
								<ChevronDown width={12.29} height={7.14} />
							</SelectButton>
						</SelectWrapper>
					</GridItem>
					<GridItem>
						<ClinicSelect
							label={'Clínica / Centro médico'}
							name="clinic"
							value={Array.isArray(values.clinic) ? values.clinic[0] || '' : values.clinic || ''}
							onChange={selected => onChangeClinic(selected, setFieldValue, values.speciality)}
							options={clinics}
							error={touched.clinic && errors.clinic}
							disabled={!values.speciality}
							placeholder={!values.speciality ? 'Selecciona especialidades primero' : 'Selecciona una clínica'}
						/>
					</GridItem>
					<DateTimeRangeFilters
						values={values}
						setFieldValue={setFieldValue}
						errors={{
							dateRange: dateRangeError,
							timeRange: timeRangeError
						}}
						startDateField="startDate"
						endDateField="endDate"
						minDate={today}
						dateLabel="Fecha del turno (Opcional)"
						timeLabel="Rango horario (Opcional)"
					/>
				</FiltersGrid>
				<ButtonRow $isMobile={isMobile}>
					<Button
						onClick={handleSubmit}
						type="submit"
						label={'Continuar'}
						size={isMobile ? 'mobile' : 'xlarge'}
						disabled={!(values.speciality && values.clinic.length > 0)}
					/>
				</ButtonRow>
			</FiltersContainer>

			<SearchCheckListModal
				isOpen={showSpecialtyModal}
				onClose={closeSpecialtyModal}
				title="¿Qué estás buscando?"
				searchPlaceholder="Busca una especialidad"
				searchValue={specialtySearch}
				onSearchChange={e => setSpecialtySearch(e.target.value)}
				options={filteredSpecialties}
				selectedValues={draftSpecialties}
				selectedTags={draftSpecialties
					.map(value => specialties.find(specialty => specialty.value === value))
					.filter(Boolean)
					.map(specialty => ({ value: specialty.value, label: specialty.label }))}
				onRemoveSelected={handleRemoveDraftSpecialty}
				cancelLabel={isMobile ? undefined : 'Cancelar'}
				confirmLabel={isMobile ? 'Buscar' : 'Confirmar'}
				onToggle={handleSelectSpecialty}
				onConfirm={handleConfirmSpecialty}
				onCancel={closeSpecialtyModal}
				confirmDisabled={draftSpecialties.length === 0}
				emptyText="No se encontraron especialidades"
				maxHeight="184px"
				isMobile={isMobile}
			/>
		</>
	)
}

export default AppointmentMultipleFilters
